import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GoogleLogo, Copy, Check, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

// Presentational-only: strip a stray leading env-key prefix (e.g. "FRONTEND_URL=")
// that may exist in the stored/production config so the UI shows a clean URL.
// Does NOT alter any stored value — display sanitization only.
function cleanUrl(value) {
  if (typeof value !== "string") return value;
  return value.replace(/^\s*[A-Z0-9_]+\s*=\s*/, "").trim();
}

function ReadonlyField({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <code
          data-testid={testId}
          className="flex-1 truncate rounded-sm border border-border bg-muted/40 px-3 py-2 font-mono text-xs text-foreground"
        >
          {value || "—"}
        </code>
        <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0"
          onClick={copy} disabled={!value} data-testid={`${testId}-copy`}>
          {copied ? <Check size={15} /> : <Copy size={15} />}
        </Button>
      </div>
    </div>
  );
}

export default function GoogleLoginSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/settings/google-oauth")
      .then((r) => {
        setCfg(r.data);
        setEnabled(!!r.data.enabled);
        setClientId(r.data.client_id || "");
        setClientSecret("");
      })
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled, client_id: clientId };
      if (clientSecret.trim()) body.client_secret = clientSecret.trim();
      const { data } = await api.put("/admin/settings/google-oauth", body);
      setCfg(data);
      setClientSecret("");
      toast.success(t("googleLogin.saved"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (user && !user.is_platform_admin) {
    return (
      <div data-testid="google-login-forbidden" className="flex items-center gap-3 rounded-sm border border-border bg-card p-6 text-sm text-muted-foreground">
        <LockKey size={18} /> {t("googleLogin.forbidden")}
      </div>
    );
  }

  const secretPlaceholder = cfg?.has_secret
    ? (cfg.client_secret_masked || t("googleLogin.secretSet"))
    : t("googleLogin.secretPh");

  return (
    <div data-testid="google-login-settings" className="max-w-3xl">
      <PageHeader title={t("googleLogin.title")} subtitle={t("googleLogin.subtitle")} />

      <div className="space-y-6 rounded-lg border border-border bg-card p-6">
        {/* Enable toggle */}
        <div className="flex items-center justify-between gap-4 rounded-sm border border-border bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-3">
            <GoogleLogo size={22} weight="bold" className="text-foreground" />
            <div>
              <div className="text-sm font-medium">{t("googleLogin.enableTitle")}</div>
              <div className="text-xs text-muted-foreground">{t("googleLogin.enableDesc")}</div>
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="google-enabled-toggle" />
        </div>

        {/* Credentials */}
        <div className="space-y-2">
          <Label htmlFor="g-client-id">{t("googleLogin.clientId")}</Label>
          <Input id="g-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)}
            placeholder="xxxxxxxx.apps.googleusercontent.com" className="bg-card font-mono text-sm"
            data-testid="google-client-id" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="g-client-secret">{t("googleLogin.clientSecret")}</Label>
          <Input id="g-client-secret" type="password" value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)} placeholder={secretPlaceholder}
            className="bg-card font-mono text-sm" data-testid="google-client-secret" autoComplete="new-password" />
          <p className="text-xs text-muted-foreground">
            {cfg?.has_secret ? t("googleLogin.secretKeepHint") : t("googleLogin.secretNewHint")}
          </p>
        </div>

        {/* Read-only Google Console values */}
        <div className="space-y-3 rounded-sm border border-dashed border-border p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("googleLogin.consoleTitle")}
          </div>
          <ReadonlyField label={t("googleLogin.redirectUri")} value={cleanUrl(cfg?.redirect_uri)} testId="google-redirect-uri" />
          <ReadonlyField label={t("googleLogin.jsOrigins")} value={(cfg?.javascript_origins || []).map(cleanUrl).join(", ")} testId="google-js-origins" />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground" data-testid="google-config-source">
            {t("googleLogin.source")}: <span className="font-mono">{cfg?.source || "—"}</span>
          </span>
          <Button onClick={save} disabled={saving} data-testid="google-save-btn">
            {saving ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
